import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRODUCT_IDS, WHATSAPP_LINK, CONTACT_EMAIL } from "@/data/site";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

export const QuoteForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("product");
  const [product, setProduct] = useState(PRODUCT_IDS.includes(preselect) ? preselect : "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const productLabel = product === "other" || !product ? "Other / Multiple" : t(`products.${product}.name`, { lng: "en" });
    setSending(true);
    setFailed(false);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Quotation Request — ${productLabel}`,
          from_name: "ANSEN Indonesia Website",
          name: form.name.value,
          company: form.company.value,
          country: form.country.value,
          product_of_interest: productLabel,
          quantity: form.quantity.value,
          message: form.message.value,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
        toast.success(t("contact.form.success"));
        form.reset();
        setProduct("");
      } else {
        console.error("Web3Forms submission failed:", data);
        setFailed(true);
        toast.error(t("contact.form.error"));
      }
    } catch (err) {
      console.error("Web3Forms submission error:", err);
      setFailed(true);
      toast.error(t("contact.form.error"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border bg-card p-10 text-center" data-testid="quote-form-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
        <p className="mt-4 text-base font-medium leading-relaxed">{t("contact.form.success")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="border bg-card p-6 sm:p-8 space-y-5" data-testid="quote-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="name">{t("contact.form.name")} *</Label>
          <Input id="name" name="name" required placeholder={t("contact.form.namePh")} className="rounded-none" data-testid="quote-input-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">{t("contact.form.company")} *</Label>
          <Input id="company" name="company" required placeholder={t("contact.form.companyPh")} className="rounded-none" data-testid="quote-input-company" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">{t("contact.form.country")} *</Label>
          <Input id="country" name="country" required placeholder={t("contact.form.countryPh")} className="rounded-none" data-testid="quote-input-country" />
        </div>
        <div className="space-y-2">
          <Label>{t("contact.form.product")} *</Label>
          <Select value={product} onValueChange={setProduct} required>
            <SelectTrigger className="rounded-none" data-testid="quote-select-product">
              <SelectValue placeholder={t("contact.form.productPh")} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {PRODUCT_IDS.map((id) => (
                <SelectItem key={id} value={id} data-testid={`quote-product-option-${id}`}>
                  {t(`products.${id}.name`)}
                </SelectItem>
              ))}
              <SelectItem value="other" data-testid="quote-product-option-other">{t("contact.form.other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="quantity">{t("contact.form.quantity")} *</Label>
          <Input id="quantity" name="quantity" required placeholder={t("contact.form.quantityPh")} className="rounded-none" data-testid="quote-input-quantity" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="message">{t("contact.form.message")}</Label>
        <Textarea id="message" name="message" rows={5} placeholder={t("contact.form.messagePh")} className="rounded-none" data-testid="quote-input-message" />
      </div>
      {failed && (
        <div className="border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="quote-form-error">
          <p className="font-medium">{t("contact.form.error")}</p>
          <div className="mt-2 flex flex-wrap gap-4">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-4" data-testid="quote-error-whatsapp-link">
              {t("contact.whatsapp")}: +62 813-3113-1286
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-primary underline underline-offset-4" data-testid="quote-error-email-link">
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      )}

      <Button type="submit" disabled={sending || !product} className="w-full rounded-none font-semibold h-11" data-testid="quote-submit-button">
        {sending ? (
          <>
            <Loader2 className="me-2 h-4 w-4 animate-spin" />
            {t("contact.form.sending")}
          </>
        ) : (
          t("contact.form.submit")
        )}
      </Button>
    </form>
  );
};
